import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { galleryItems, workFilters } from "@/mocks/site";

export default function Work() {
  const [activeFilter, setActiveFilter] = useState("All");

  const filtered = useMemo(() => {
    if (activeFilter === "All") return galleryItems;
    return galleryItems.filter((item) => item.category === activeFilter);
  }, [activeFilter]);

  return (
    <>
      <section className="bg-background-50 px-4 pb-4 pt-28 text-center md:px-10 md:pb-6 md:pt-36">
        <Reveal>
          <p className="font-label text-xs tracking-[0.25em] uppercase text-accent-700">
            Selected Work
          </p>
          <h1 className="mt-5 font-heading text-3xl font-extrabold uppercase tracking-tight text-foreground-950 md:text-5xl">
            Work
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-foreground-600">
            Award-winning brand films, commercials, and fundraising stories. A selection of
            the projects we're proud of.
          </p>
        </Reveal>
      </section>

      <section className="bg-background-50 px-4 pb-12 md:px-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-3">
          {workFilters.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setActiveFilter(filter)}
              className={`whitespace-nowrap rounded-full border px-4 py-2 font-label text-xs tracking-[0.14em] uppercase transition-colors duration-200 cursor-pointer ${
                activeFilter === filter
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-foreground-950"
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-background-50 px-4 pb-20 md:px-10 md:pb-28">
        {filtered.length > 0 ? (
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item, index) => (
              <Reveal key={item.id} delay={(index % 3) * 100}>
                <article className="group">
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg">
                    <img
                      src={item.image}
                      alt={item.alt}
                      title={`${item.title} — video production`}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                    />
                    {item.award && (
                      <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-3 py-1 font-label text-[10px] tracking-[0.15em] uppercase text-accent-950">
                        {item.award}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-heading text-lg text-foreground-950">
                        {item.title}
                      </h2>
                      <p className="mt-1 text-xs uppercase tracking-[0.14em] text-foreground-500">
                        {item.category} · {item.location}
                      </p>
                    </div>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors group-hover:border-accent-400 group-hover:text-accent-700">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="py-16 text-center text-foreground-500">
            No projects in this category yet — check back soon.
          </p>
        )}
      </section>

      <section className="bg-foreground-950 px-4 py-20 text-center md:px-10 md:py-24">
        <Reveal>
          <h2 className="mx-auto max-w-3xl font-heading text-3xl tracking-tight text-background-50 md:text-4xl">
            Your story could be next.
          </h2>
          <Link
            to="/contact"
            className="mt-8 inline-block whitespace-nowrap rounded-md bg-background-50 px-8 py-4 font-label text-xs tracking-[0.2em] uppercase text-foreground-950 transition-colors duration-200 hover:bg-accent-400 hover:text-accent-950"
          >
            Start the Conversation
          </Link>
        </Reveal>
      </section>
    </>
  );
}